import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../i18n';
import { client } from '../api/client';
import { useAppStore } from '../store/appStore';

interface PaymentStatus {
  orderId: string;
  status: string;
  amount: number;
  tokens: number;
  createdAt: string;
  completedAt: string | null;
}

export function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useI18n();
  const { setTokenBalance } = useAppStore();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orderId = searchParams.get('OrderID') || searchParams.get('orderId') || '';

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!orderId) {
        setLoading(false);
        setError(language === 'sr' ? 'Nedostaje broj narudžbine' : 'Missing order ID');
        return;
      }

      try {
        const response = await client.api.fetch(`/api/payment/status?orderId=${orderId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setPaymentStatus(data.data);
          } else {
            setError(data.error || 'Greška pri učitavanju');
          }

          // Refresh token balance
          try {
            const tokenRes = await client.api.fetch('/api/tokens');
            const tokenData = await tokenRes.json();
            if (tokenData.data?.balance !== undefined) {
              setTokenBalance(tokenData.data.balance);
            }
          } catch (e) {
            console.error('Error refreshing token balance:', e);
          }
        } else {
          const data = await response.json().catch(() => ({}));
          setError(data.error || 'Transakcija nije pronađena');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setError(language === 'sr' ? 'Greška pri komunikaciji sa serverom' : 'Server communication error');
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [orderId, setTokenBalance, language]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center"
        >
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </motion.div>

        <h1 className="text-2xl font-bold text-green-700 dark:text-green-400 mb-3">
          {language === 'sr' ? 'Plaćanje uspešno!' : 'Payment Successful!'}
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-6">
          {language === 'sr' 
            ? 'Vaša kupovina tokena je uspešno realizovana.' 
            : 'Your token purchase has been completed successfully.'}
        </p>

        {loading ? (
          <div className="animate-pulse bg-slate-100 dark:bg-slate-700 rounded-xl p-6 mb-6">
            <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-3/4 mx-auto mb-2"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-1/2 mx-auto"></div>
          </div>
        ) : paymentStatus ? (
          <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-6 mb-6 text-left">
            <div className="flex justify-between py-2 border-b border-green-100 dark:border-green-800">
              <span className="text-slate-600 dark:text-slate-400 text-sm">
                {language === 'sr' ? 'Kupljeno tokena:' : 'Tokens purchased:'}
              </span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {paymentStatus.tokens} tokena
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-green-100 dark:border-green-800">
              <span className="text-slate-600 dark:text-slate-400 text-sm">
                {language === 'sr' ? 'Iznos:' : 'Amount:'}
              </span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {paymentStatus.amount.toLocaleString()} RSD
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-600 dark:text-slate-400 text-sm">
                {language === 'sr' ? 'Status:' : 'Status:'}
              </span>
              <span className="text-green-600 dark:text-green-400 font-medium">
                {paymentStatus.status === 'approved' 
                  ? (language === 'sr' ? '✓ Odobreno' : '✓ Approved')
                  : paymentStatus.status === 'pending'
                  ? (language === 'sr' ? '⏳ Čeka se potvrda...' : '⏳ Pending...')
                  : (language === 'sr' ? 'Obrađuje se...' : 'Processing...')}
              </span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6">
            <p className="text-amber-700 dark:text-amber-400 text-sm mb-2">
              {error}
            </p>
            <p className="text-slate-500 dark:text-slate-400 text-xs">
              {language === 'sr' 
                ? 'Vaša transakcija je možda još uvek u obradi. Proverite stanje tokena u profilu ili kontaktirajte podršku.' 
                : 'Your transaction may still be processing. Check your token balance in your profile or contact support.'}
            </p>
          </div>
        ) : (
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 mb-6">
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {language === 'sr' 
                ? 'Podaci o transakciji se učitavaju. Ako ste završili plaćanje, tokeni će biti dodati na vaš nalog.' 
                : 'Transaction data is loading. If you completed payment, tokens will be added to your account.'}
            </p>
          </div>
        )}

        {orderId && (
          <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 mb-6">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {language === 'sr' ? 'Broj narudžbine:' : 'Order ID:'}
            </p>
            <p className="font-mono text-sm text-slate-700 dark:text-slate-300">{orderId}</p>
          </div>
        )}

        <button
          onClick={() => navigate('/kupi-tokene')}
          className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl"
        >
          {language === 'sr' ? 'Kupi još tokena' : 'Buy More Tokens'}
        </button>

        <button
          onClick={() => navigate('/profil')}
          className="w-full mt-3 py-3 px-6 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-xl transition-colors"
        >
          {language === 'sr' ? 'Idi na profil' : 'Go to Profile'}
        </button>

        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          {language === 'sr' 
            ? 'Potvrda o kupovini je poslata na vašu email adresu.' 
            : 'Purchase confirmation has been sent to your email.'}
        </p>
      </motion.div>
    </div>
  );
}
